import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import { AppyError, callApi } from '../../../../Appy';
import { currentUserStore } from '../../../../CurrentUserStore';
import ErrorActionableReaction, { EAProps, handleError } from '../../../../ErrorActionableReaction';
import { MissingLinkError, findLinkT } from '../../../../LinkyLinky';
import { Link } from '../../../../Resource/Types';
import { UserGroup } from '../../../Form/GroupInputWithSuggestions/Types';
import { importOptionsResourceDecoder, importResultResourceDecoder } from './Decoders';
import {
  areLicensesAuditor,
  ImportOptionsResource,
  ImportResultResource,
  isSelectedLicense,
  State,
} from './Types';
import ImportParticipantsV2Store from './index';

interface Props extends EAProps<ImportParticipantsV2Store> {}

type ImportParticipantsV2Error = AppyError | MissingLinkError;

const handleOptionsError =
  (store: ImportParticipantsV2Store) => (error: ImportParticipantsV2Error) => {
    switch (error.kind) {
      case 'missing-link-error':
        warnAndNotify('ImportParticipantsV2Reactions', 'Missing link error', { error });
        store.setOptionsLoadError('Import options link is unavailable');
        break;
      default:
        handleError(store, error);
    }
  };

const handleSubmitError =
  (store: ImportParticipantsV2Store) => (error: ImportParticipantsV2Error) => {
    switch (error.kind) {
      case 'missing-link-error':
        warnAndNotify('ImportParticipantsV2Reactions', 'Missing link error', { error });
        store.setError('Import participants link is unavailable');
        break;
      default:
        handleError(store, error);
    }
  };

const buildImportPayload = (state: State) => {
  if (state.kind !== 'submitting') {
    return { participants: [], user_groups: [], licenses: [], are_licenses_auditor: false };
  }

  const userGroups = state.selectedGroups
    .filter(
      (g): g is { kind: 'selected'; group: UserGroup } | { kind: 'new'; name: string } =>
        g.kind === 'selected' || g.kind === 'new',
    )
    .map((g) => {
      if (g.kind === 'selected') {
        return { id: g.group.id, name: g.group.name };
      }
      return { name: g.name };
    });

  const licenseIds = state.selectedLicenses
    .filter(isSelectedLicense)
    .map((l) => ({ id: l.license.id }));

  return {
    participants: state.participants.map((p) => {
      const base = { email: p.email, firstname: p.firstname, lastname: p.lastname };
      return p.login_type.cata({
        Just: (lt) => ({ ...base, login_type: lt }),
        Nothing: () => base,
      });
    }),
    user_groups: userGroups,
    licenses: licenseIds,
    are_licenses_auditor: areLicensesAuditor(state.selectedLicenses),
  };
};

class ImportParticipantsV2Reactions extends ErrorActionableReaction<
  ImportParticipantsV2Store,
  State,
  Props
> {
  tester = () => this.props.store.state;

  effect = (state: State) => {
    const { store } = this.props;

    switch (state.kind) {
      case 'loading-options':
        Task.succeed<ImportParticipantsV2Error, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(findLinkT('import-participants-options'))
          .andThen(callApi(importOptionsResourceDecoder, {}))
          .fork(handleOptionsError(store), (resource: ImportOptionsResource) => {
            store.setOptionsReady(resource);
          });
        break;
      case 'submitting':
        Task.succeed<ImportParticipantsV2Error, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(findLinkT('import-multiple-participants'))
          .andThen(callApi(importResultResourceDecoder, buildImportPayload(state)))
          .fork(handleSubmitError(store), (resource: ImportResultResource) => {
            store.submitSucceeded(resource);
          });
        break;
      case 'waiting':
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submit-failed':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default ImportParticipantsV2Reactions;
